import { Text } from 'preact-i18n';
import { formatPriceCompact } from '../utils/priceFormatters';
import { getMinPriceByLevel } from '../utils/priceLevelStats';
import isDateBlocked from '../utils/isDateBlocked';
import NoTripsIcon from './NoTripsIcon';

const LEVELS = ['cheap', 'normal', 'expensive'];

/**
 * Calendar price-level legend row.
 *
 * Renders one chip per price level that has at least one available day with a
 * known min price in `availability` (each chip shows `$NNN +`), followed by the
 * price disclaimer whenever at least one chip is present.
 *
 * When every day in the range is blocked — `available: false` and settled
 * (`state !== 'pending'`) — it shows the "no-trips" indicator instead. Chips and
 * "no-trips" are mutually exclusive, and the price disclaimer is omitted in the
 * no-trips case because there are no prices to disclaim.
 *
 * Returns `null` when there is nothing to show (e.g. while the window is still
 * warming up), so the footer never renders an empty legend row.
 *
 * Pass `className` to scope visibility (desktop-only or mobile-only).
 */
const PriceLevelLegend = ({ availability, className = '' }) => {
  const mins = getMinPriceByLevel(availability);
  const visibleLevels = LEVELS.filter(level => mins[level] !== null);
  const days = Object.values(availability || {});
  const hasPriceChips = visibleLevels.length > 0;
  const showNoTrips = days.length > 0 && days.every(isDateBlocked);

  if (!hasPriceChips && !showNoTrips) return null;

  return (
    <div className="udp-continue-footer__legend-row">
      <div className={`udp-price-legend ${className}`.trim()}>
        {visibleLevels.map(level => (
          <span
            key={level}
            className={`udp-price-legend__chip udp-price-legend__chip--${level}`}
          >
            {formatPriceCompact(mins[level])} +
          </span>
        ))}
        {showNoTrips && (
          <span className="udp-price-legend__no-data">
            <NoTripsIcon className="udp-price-legend__no-data-icon" />
            <Text id="label.price_level_no_trips" />
          </span>
        )}
      </div>
      {hasPriceChips && (
        <p className="udp-continue-footer__disclaimer">
          <Text id="label.price_level_disclaimer" />
        </p>
      )}
    </div>
  );
};

export default PriceLevelLegend;
